import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { genders } from 'constants/campaign'
import { RootState } from 'store'
import { SpecialityNiche } from 'types'
import { formatNumber } from 'utils/helpers'

export const FILTER_LABELS: Record<string, string> = {
    gender: 'Gender',
    follower_count: 'Followers',
    average_views: 'Average Views',
    niches: 'Interest',
    speciality_niches: 'Interest',
    cities: '',
    regions: 'Region',
    age_range: 'Age',
    age_min: 'Age',
    age_max: 'Age',
    languages: 'Lang',
    topics: 'Topic',
    exclude_topics: 'No topic',
    audience_gender: 'Audience Gender',
    audience_cities: 'Audience Location',
    audience_age_groups: 'Audience Age',
    min_engagement_score: 'Engagement',
    min_authenticity_score: 'Authenticity',
}

interface AppliedFilterBadgeProps {
    filterKey: string
    value: string | number
    onRemove: () => void
}

const AppliedFilterBadge = ({ filterKey, value, onRemove }: AppliedFilterBadgeProps) => {
    const nicheList = useSelector((state: RootState) => state.auth.master?.niche_list)
    const specialityNiches = useSelector((state: RootState) => state.auth.master?.speciality_niches)
    const languages = useSelector((state: RootState) => state.master.languages)

    const label = FILTER_LABELS[filterKey] ?? filterKey.replace(/_/g, ' ')

    const displayValue = useMemo(() => {
        const id = String(value)

        switch (filterKey) {
            case 'gender':
            case 'audience_gender':
                return genders[value as keyof typeof genders] ?? id
            case 'niches':
                return nicheList?.[id] ?? nicheList?.[String(id)] ?? id
            case 'speciality_niches':
                return specialityNiches?.find((n) => Number(n.id) === Number(id))?.name ?? id
            case 'languages':
                return languages?.[id] ?? languages?.[String(id)] ?? id
            case 'follower_count':
            case 'average_views':
                return `${formatNumber(Number(value))}+`
            case 'age_range':
                return id
            case 'age_min':
                return `${id}+`
            case 'age_max':
                return `≤${id}`
            case 'min_engagement_score':
                return `≥${id}`
            case 'min_authenticity_score':
                return `≥${id}%`
            default:
                return id
        }
    }, [filterKey, value, nicheList, specialityNiches, languages])

    const title = label ? `Remove ${label}: ${displayValue}` : `Remove ${displayValue}`

    return (
        <button
            type="button"
            className={`filter-badge-button applied ${filterKey}`}
            onClick={onRemove}
            title={title}
            aria-label={title}
        >
            <span className="applied-filter-badge__text">
                {label ? <span className="applied-filter-badge__label">{label}</span> : null}
                <span className="applied-filter-badge__value">{displayValue}</span>
            </span>
            <i
                className="mdi mdi-close"
                aria-hidden
                onClick={(e) => {
                    e.stopPropagation()
                    onRemove()
                }}
            />
        </button>
    )
}

export default AppliedFilterBadge
